'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import logNullStateCTAClicked from '@alltrails/amplitude/events/logNullStateCTAClicked';
import logNullStateViewed from '@alltrails/amplitude/events/logNullStateViewed';
import NullState from '@alltrails/denali/components/NullState';
import Tent from '@alltrails/denali/illustrations/Tent';
import styles from './TrailConditionsErrorCard.module.scss';

type TrailConditionsProps = {
  className?: string;
  onRetry: () => void;
  showIllustration?: boolean;
  testId?: string;
  trailId: number;
};

const TrailConditionsErrorCard = ({ className, onRetry, showIllustration, testId, trailId }: TrailConditionsProps) => {
  const intl = useIntl();

  useEffect(() => {
    logNullStateViewed({ null_location: 'trail conditions card', null_state: 'server error', trail_id: trailId });
    // Only log on first render
  }, []);

  const hasLoggedRetryEvent = useRef(false);
  const onClick = useCallback(() => {
    if (!hasLoggedRetryEvent.current) {
      hasLoggedRetryEvent.current = true;
      logNullStateCTAClicked({
        null_action: 'retry',
        null_location: 'trail conditions card',
        null_state: 'server error',
        trail_id: trailId
      });
    }
    onRetry();
  }, [onRetry, trailId]);

  return (
    <div className={classNames(styles.container, className)}>
      <NullState
        buttons={{ default: { onClick, testId, text: intl.formatMessage({ defaultMessage: 'Retry' }) } }}
        className={styles.nullState}
        description={intl.formatMessage({ defaultMessage: 'We’re having trouble loading conditions for this trail' })}
        illustration={showIllustration ? { Component: Tent } : undefined}
        noTopMargin
        title={intl.formatMessage({ defaultMessage: 'Let’s try that again' })}
      />
    </div>
  );
};

export default TrailConditionsErrorCard;
